import React, {useState, useEffect, useCallback} from 'react';
import {
  Grid,
  GridCol,
  Input,
  Label,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {Icon} from '@flixbus/honeycomb-icons-react';
import {RegionalCostsDriverCost} from '@/Pages/RegionalCosts/store/types';
import ContentLoader from '@/Pages/Common/ContentLoader';
import CpcPanel from '@/Pages/Common/CpcPanel';
import {validateInputNumber} from '@/Pages/Common/Utils';
import {DriverCostFieldConfig} from '../DriverCostsTable/DriverCostsTable.types';
import {
  DriverCostsPanelData,
  DriverCostsPanelProps
} from './DriverCostsPanel.types';

const DriverCostsPanel: React.FC<DriverCostsPanelProps> = ({
  country,
  year,
  panelData,
  config,
  updateFn,
  onClose
}) => {
  const {half: spaceHalf} = spaceHelpers();

  const [loading, setLoading] = useState<boolean>(false);
  const [localData, setLocalData] = useState<DriverCostsPanelData | null>(null);

  useEffect(() => {
    setLocalData(panelData);
  }, [panelData]);

  const onChangeInput = useCallback(
    (value: string, fieldConfig: DriverCostFieldConfig) => {
      const newValue = validateInputNumber(
        value,
        String(localData?.[fieldConfig.key]),
        fieldConfig.integerDigitsCount,
        fieldConfig.fractionDigitsCount
      );

      setLocalData(
        prevData =>
          ({
            ...prevData,
            [fieldConfig.key]: newValue ?? null
          } as DriverCostsPanelData)
      );
    },
    [localData]
  );

  const labelRendered = useCallback(
    (config: DriverCostFieldConfig) => {
      return (
        <Label
          text={
            <>
              {config.name} ({config.unit})
              {config.icon && (
                <Icon InlineIcon={config.icon} extraClasses={spaceHalf.left} />
              )}
              :
            </>
          }
          extraClasses="flex-row"
        />
      );
    },
    [spaceHalf.left]
  );

  const inputRenderer = useCallback(
    (config: DriverCostFieldConfig, localData: DriverCostsPanelData) => (
      <Input
        key={`driver-cost-panel-${config.key}`}
        name={`driver-cost-panel-${config.key}`}
        id={`driver-cost-panel-${config.key}`}
        extraClasses="driver-cost-panel-input"
        type="number"
        step="0.1"
        label={labelRendered(config)}
        inlineLabelLeft={config.unit}
        value={localData[config.key] ?? ''}
        onChange={event => onChangeInput(event.target.value, config)}
      />
    ),
    [labelRendered, onChangeInput]
  );

  const fieldsRendered = useCallback(() => {
    if (!localData) return null;

    return config.map(field => (
      <GridCol key={field.key} size={12}>
        {inputRenderer(field, localData)}
      </GridCol>
    ));
  }, [config, localData, inputRenderer]);

  const convertPanelDataToDriverCosts = useCallback(
    (panelData: DriverCostsPanelData): RegionalCostsDriverCost =>
      ({
        ...panelData,
        currency: country.currency,
        year
      } as RegionalCostsDriverCost),
    [country.currency, year]
  );

  const onSuccess = useCallback(async () => {
    if (!localData) return;

    setLoading(true);

    const updatedCosts = convertPanelDataToDriverCosts(localData);
    await updateFn(updatedCosts);

    setLoading(false);
    onClose();
  }, [convertPanelDataToDriverCosts, localData, onClose, updateFn]);

  return (
    <CpcPanel
      title={`Update driver cost for ${country.name}`}
      activeAction={!!localData}
      closeAction={onClose}
      saveAction={onSuccess}
      extraClasses="driver-cost-panel"
      loading={loading}
    >
      {localData ? (
        <Grid extraClasses="gap-2">{fieldsRendered()}</Grid>
      ) : (
        <ContentLoader />
      )}
    </CpcPanel>
  );
};

export default React.memo(DriverCostsPanel);
